/**
 * Slowness the browser saw, reported to the server so it lands in the same log as the server's
 * own request lines (see `orchestrator/src/ui/client-timings.ts`).
 *
 * Only what crosses a threshold is sent, batched, and never retried: this exists to answer "it
 * was slow at 3:40" after the fact, and it must not itself add load or noise.
 */

type ClientTimingKind =
  | 'api'
  | 'page-load'
  | 'long-task'
  | 'interaction'
  | 'stream-dropped'
  | 'stream-reconnected'
  | 'stream-stale';

interface ClientTimingEvent {
  kind: ClientTimingKind;
  ms: number;
  /** API path, or what a slow interaction was (`click`, `keydown`). */
  path?: string;
  method?: string;
  status?: number;
  /** The tab was in the background, where browsers throttle timers. */
  hidden?: boolean;
  at: number;
}

/** A dashboard request this slow, from the browser's side, is reported. */
const SLOW_API_MS = 2_000;
const SLOW_PAGE_LOAD_MS = 3_000;
/** A main-thread task this long froze the tab; the browser itself calls 50 ms "long". */
const FROZEN_TASK_MS = 1_000;
/** From a click or key press to the next paint. */
const SLOW_INTERACTION_MS = 1_000;
const FLUSH_MS = 10_000;
const MAX_QUEUED = 50;
const ENDPOINT = '/api/client-timings';

const queue: ClientTimingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;

const inBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

export function reportClientTiming(event: Omit<ClientTimingEvent, 'at' | 'hidden'>): void {
  if (!inBrowser() || queue.length >= MAX_QUEUED) return;
  queue.push({
    ...event,
    ms: Math.round(event.ms),
    at: Date.now(),
    ...(document.visibilityState === 'hidden' ? { hidden: true } : {}),
  });
  flushTimer ??= setTimeout(flushClientTimings, FLUSH_MS);
}

/** Called by the API client for every request; reports the slow ones. */
export function recordApiTiming(path: string, method: string, ms: number, status: number): void {
  if (ms < SLOW_API_MS || path.startsWith(ENDPOINT)) return;
  reportClientTiming({ kind: 'api', path, method, ms, status });
}

function flushClientTimings(): void {
  if (flushTimer !== undefined) clearTimeout(flushTimer);
  flushTimer = undefined;
  if (queue.length === 0) return;
  const body = JSON.stringify({ page: window.location.pathname, events: queue.splice(0) });
  // A beacon survives the tab closing, which is often right after the slow moment.
  const sent =
    typeof navigator.sendBeacon === 'function' &&
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
  if (!sent) {
    void fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body,
    }).catch(() => undefined);
  }
}

function observe(type: string, threshold: number, report: (entry: PerformanceEntry) => void): void {
  if (typeof PerformanceObserver === 'undefined') return;
  if (!PerformanceObserver.supportedEntryTypes.includes(type)) return;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) if (entry.duration >= threshold) report(entry);
  });
  // `durationThreshold` only applies to event timing; other types ignore it.
  observer.observe({ type, buffered: true, durationThreshold: 104 } as PerformanceObserverInit);
}

/** Start watching page load, frozen tasks and slow interactions. Once, at startup. */
export function startClientTiming(): void {
  if (!inBrowser()) return;

  window.addEventListener('load', () => {
    // After the load handler returns, so `loadEventEnd` is filled in.
    setTimeout(() => {
      const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navigation && navigation.loadEventEnd >= SLOW_PAGE_LOAD_MS) {
        reportClientTiming({ kind: 'page-load', ms: navigation.loadEventEnd, path: window.location.pathname });
      }
    }, 0);
  }, { once: true });

  observe('longtask', FROZEN_TASK_MS, (entry) => {
    reportClientTiming({ kind: 'long-task', ms: entry.duration });
  });
  // One click is a pointerdown, a pointerup and a click, all sharing an `interactionId` and
  // all as slow as each other. Hover events carry no id and are not interactions at all.
  let lastInteractionId = 0;
  observe('event', SLOW_INTERACTION_MS, (entry) => {
    const id = (entry as PerformanceEntry & { interactionId?: number }).interactionId ?? 0;
    if (id === 0 || id === lastInteractionId) return;
    lastInteractionId = id;
    reportClientTiming({ kind: 'interaction', ms: entry.duration, path: entry.name });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushClientTimings();
  });
}
